import React, { useEffect, useState, useRef } from 'react';
import { Play, Pause } from 'lucide-react';
import { motion } from 'framer-motion';
interface AudioCardProps {
  id: string;
  title: string;
  description: string;
  durationStr: string;
  index: number;
  audioUrl?: string;
  playingId: string | null;
  onPlay: (id: string | null) => void;
}
const parseDuration = (duration: string) => {
  const [mins, secs] = duration.split(':').map(Number);
  return mins * 60 + secs;
};
const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};
export function AudioCard({
  id,
  title,
  description,
  durationStr,
  index,
  audioUrl,
  playingId,
  onPlay
}: AudioCardProps) {
  const isPlaying = playingId === id;
  const isOtherPlaying = playingId !== null && playingId !== id;
  const [currentTime, setCurrentTime] = useState(0);
  const totalSeconds = parseDuration(durationStr);
  const audioRef = useRef<HTMLAudioElement>(null);
  // Handle real audio play/pause when isPlaying changes from parent
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      if (isPlaying) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('Playback prevented:', error);
          });
        }
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, audioUrl]);
  // Simulate audio playback ONLY if there is no real audioUrl
  useEffect(() => {
    if (audioUrl) return;
    let interval: NodeJS.Timeout;
    if (isPlaying && currentTime < totalSeconds) {
      interval = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= totalSeconds - 1) {
            onPlay(null);
            return totalSeconds;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentTime, totalSeconds, audioUrl, onPlay]);
  const togglePlay = () => {
    if (isPlaying) {
      onPlay(null); // Pause
    } else {
      // Always reset to beginning
      setCurrentTime(0);
      if (audioUrl && audioRef.current) {
        audioRef.current.currentTime = 0;
      }
      onPlay(id); // Play this one, which will pause others
    }
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = Number(e.target.value);
    setCurrentTime(newTime);
    if (audioUrl && audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };
  const progressPercent = currentTime / totalSeconds * 100;
  return <motion.div onClick={togglePlay} initial={{
    opacity: 0,
    y: 24
  }} animate={{
    opacity: isOtherPlaying ? 0.25 : 1,
    y: 0,
    scale: isPlaying ? 1.03 : isOtherPlaying ? 0.95 : 1,
    filter: isOtherPlaying ? 'blur(1.5px)' : 'blur(0px)'
  }} transition={{
    duration: 0.7,
    delay: index === 0 && !playingId ? 0.15 : 0,
    ease: [0.25, 0.46, 0.45, 0.94]
  }} whileHover={{
    y: isOtherPlaying ? 0 : -2,
    opacity: isOtherPlaying ? 0.6 : 1,
    filter: isOtherPlaying ? 'blur(0px)' : 'blur(0px)'
  }} className={`relative rounded-3xl p-7 pb-6 transition-all duration-700 group cursor-pointer overflow-hidden ${isPlaying ? 'bg-white/80 backdrop-blur-xl border border-white/60 shadow-[0_16px_40px_-8px_rgba(44,42,40,0.15),inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(255,255,255,0.3)] z-10' : 'bg-gemma-card border border-gemma-accent/[0.12] shadow-[0_4px_20px_-4px_rgba(44,42,40,0.06)] hover:shadow-[0_12px_32px_-4px_rgba(44,42,40,0.1)] z-0'}`} data-id="element-4">
      {/* Crystal Shine Effect when playing */}
      {isPlaying && <>
          {/* Animated light sweep across the card */}
          <motion.div className="absolute inset-0 z-[1] pointer-events-none" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5
      }} data-id="element-5">
            <motion.div className="absolute top-0 -left-[100%] w-[60%] h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-20deg]" animate={{
          left: ['-100%', '200%']
        }} transition={{
          duration: 3,
          repeat: Infinity,
          repeatDelay: 4,
          ease: 'easeInOut'
        }} data-id="element-6" />
          </motion.div>

          {/* Top edge crystal highlight */}
          <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent z-[2] pointer-events-none" data-id="element-7" />

          {/* Subtle inner glow */}
          <motion.div className="absolute inset-0 rounded-3xl z-[0] pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.08) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(154,106,70,0.06) 0%, transparent 60%)'
      }} animate={{
        opacity: [0.6, 1, 0.6]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-8" />

          {/* Angelic Halo Glow behind card */}
          <motion.div className="absolute -inset-3 bg-gemma-gold/20 rounded-[2.5rem] blur-xl z-[-1]" animate={{
        opacity: [0.3, 0.7, 0.3],
        scale: [0.98, 1.02, 0.98]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-9" />

          {/* Bottom edge soft reflection */}
          <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-gemma-gold/30 to-transparent z-[2] pointer-events-none" data-id="element-10" />
        </>}

      {audioUrl && <audio ref={audioRef} src={audioUrl} onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)} onEnded={() => {
      onPlay(null);
      setCurrentTime(totalSeconds);
    }} data-id="element-11" />}

      {/* Top row: number + title/description */}
      <div className="flex items-start gap-4 mb-5 relative z-[3]" data-id="element-12">
        {/* Number */}
        <span className={`font-serif text-[2rem] leading-none select-none pt-0.5 tracking-tight transition-colors duration-500 ${isPlaying ? 'text-gemma-accent/80' : 'text-gemma-accent/40'}`} data-id="element-13">
          {id}
        </span>

        <div className="flex-1 min-w-0 pt-1" data-id="element-14">
          <h3 className="font-serif text-[17px] text-gemma-charcoal font-medium leading-snug mb-1 tracking-[-0.01em]" data-id="element-15">
            {title}
          </h3>
          <p className="font-sans text-[12px] text-gemma-charcoal/70 font-light leading-relaxed" data-id="element-16">
            {description}
          </p>
        </div>
      </div>

      {/* Thin separator */}
      <div className={`h-px mb-4 mx-1 relative z-[3] ${isPlaying ? 'bg-gemma-accent/20' : 'bg-gemma-accent/[0.15]'}`} data-id="element-17" />

      {/* Player Controls */}
      <div className="flex items-center gap-3.5 relative z-[3]" data-id="element-18">
        <motion.button onClick={e => {
        e.stopPropagation();
        togglePlay();
      }} whileTap={{
        scale: 0.92
      }} className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 text-white" style={{
        background: 'linear-gradient(135deg, #B87333, #C4956A, #B87333)',
        boxShadow: isPlaying ? '0 2px 8px -2px rgba(154,106,70,0.4)' : '0 1px 4px -1px rgba(154,106,70,0.25)'
      }} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-19">
          {isPlaying ? <Pause className="w-4 h-4 fill-current relative z-10" data-id="element-20" /> : <Play className="w-4 h-4 fill-current ml-0.5 relative z-10" data-id="element-21" />}
        </motion.button>

        <div className="flex-1 flex items-center gap-3" data-id="element-22">
          <div className="relative w-full h-5 flex items-center" data-id="element-23">
            {/* Track background */}
            <div className="absolute left-0 right-0 h-[4px] bg-gemma-accent/[0.15] rounded-full" data-id="element-24" />
            {/* Filled progress */}
            <div className="absolute left-0 h-[4px] bg-gemma-accent/80 rounded-full transition-[width] duration-150 ease-out" style={{
            width: `${progressPercent}%`
          }} data-id="element-25" />
            <input type="range" min="0" max={totalSeconds} value={currentTime} onChange={handleSeek} onClick={e => e.stopPropagation()} className="w-full z-10 cursor-pointer" aria-label="Seek audio" data-id="element-26" />
          </div>
          <span className="font-sans text-[11px] text-gemma-charcoal/60 tabular-nums shrink-0 w-8 text-right font-medium" data-id="element-27">
            {isPlaying || currentTime > 0 ? formatTime(currentTime) : durationStr}
          </span>
        </div>
      </div>
    </motion.div>;
}